// The Big Knock's screens (sprint 29 item 3): the hall banner, the door panel with its live countdown and crowd count, the wait for the
// reveal, and the big reveal. DOM-light on purpose: `doc` makes the elements, so a test can hold them with a small fake. Words are all
// game/bigknock-copy.js and game/bigknock-window.js. Reduced motion: no rays, no steps, no animation; the finished reveal at once.
import { BIGKNOCK_COPY as C, shareText } from '../game/bigknock-copy.js';
import { windowLine, windowStated } from '../game/bigknock-window.js';
import { BIGKNOCK } from '../game/bigknock-core.js';
import { opened, mark, burst, glow, keyringRing, ringSlots, svgUrl, ART_PX, BURST, ZOOM, WIN, OPEN, VIEW, GLOW, MARK, place } from './mail-art.js';

export const REVEAL_STEP_MS = 1300; // the crowd, then your result, each this long after the one before
export const VISITOR_MS = { alien: 2600, human: 3600 }; // the hard visitor's own reveal plays this long before the words come (client/bigknock-visitor.js)
export const CROWD_EVERY_MS = 5000; // the open panel asks the server for a fresh head count this often
const localTz = ms => new Date(ms).getTimezoneOffset();

// Whole seconds left on the clock a phase counts down to; `at` is when the server's seconds arrived, `now` the page's clock (both performance.now()).
export function leftOf(st, now) {
  if (!st) return null;
  const gone = Math.floor((now - st.at) / 1000);
  return { toOpen: Math.max(0, st.seconds.toOpen - gone), toClose: Math.max(0, st.seconds.toClose - gone), toReveal: Math.max(0, st.seconds.toReveal - gone) };
}
// The line the banner and the panel show for the phase.
export function clockLine(st, left) {
  if (!st) return '';
  if (st.phase === 'announced') return left.toOpen > 0 ? C.countdown.toOpen(left.toOpen) : C.countdown.now;
  if (st.phase === 'open') return left.toClose > 0 ? C.countdown.open(left.toClose) : C.countdown.deciding(left.toReveal);
  return C.countdown.deciding(left.toReveal);
}
// Which phase the page shows now: the server's, moved on by its own clock until the server says so (it does within a second).
export function shownPhase(st, left) {
  if (!st) return null;
  if (st.phase === 'announced' && left.toOpen <= 0) return 'open';
  if (st.phase === 'open' && left.toClose <= 0) return 'deciding';
  return st.phase;
}
// What you did, in the reveal's words. you: the server's own result for this player (null: not at the door).
export function youLines(you) {
  if (!you) return { head: C.you.absent, gain: [] };
  const o = you.outcome;
  if (o === 'winner') return { head: C.you.winner, gain: [C.gain.big(you.points)] };
  if (o === 'right') return { head: you.undrawn ? C.you.undrawn(BIGKNOCK.minDoors) : C.you.right, gain: [C.gain.points(you.points)] };
  if (o === 'fooled') return { head: C.you.fooled, gain: [C.gain.fragments(you.fragments), ...(you.keyring ? [C.gain.keyring(you.keyring.have, you.keyring.of)] : [])] };
  if (o === 'timeout') return { head: C.you.timeout, gain: [] };
  return { head: C.you.notCounted, gain: [] };
}
export const shareOutcome = you => (you?.outcome === 'winner' ? 'winner' : you?.outcome === 'right' ? 'right' : you?.outcome === 'fooled' ? 'fooled' : null);
export const shareFor = m => { const o = shareOutcome(m.you); return o ? shareText(m.n, o, { right: m.right, real: m.real }) : null; };
// the wait: the clock line, whether doors are still open
export const waitClock = (st, left) => (shownPhase(st, left) === 'open' ? C.wait.open(left.toClose) : C.waiting(left.toReveal));
// The next-event window as a line (game/bigknock-window.js): the public rule, never the drawn start. null: none known.
// zoneAt (the page passes it): the window with its day and time zone named, as windowStated says it; without it the short form.
export const nextLine = (next, now, tzAt = localTz, zoneAt = null) => (next ? (zoneAt ? windowStated(next, now, next.n, tzAt, zoneAt) : windowLine(next, now, next.n, tzAt)) : C.noNext);

// The beats of the reveal: [{ at, show }]. The hard visitor plays first (the art box, from 0), then the kind, the crowd and your result.
// Reduced motion shows the finished screen at once.
export const revealPlan = ({ reduced = false, kind = 'alien' } = {}) => {
  if (reduced) return [{ at: 0, show: 'all' }];
  const v = VISITOR_MS[kind] ?? VISITOR_MS.alien;
  return [{ at: 0, show: 'visitor' }, { at: v, show: 'kind' }, { at: v + REVEAL_STEP_MS, show: 'crowd' }, { at: v + REVEAL_STEP_MS * 2, show: 'you' }];
};

// the art is drawn at the stage's own 2x (mail-art.js ART_PX); the burst's centre is the opened envelope's, the box's centre is where both sit
const css = (e, o) => { if (e.style) Object.assign(e.style, o); else e.css = { ...e.css, ...o }; };
export const ART_BOX = { h: 200 }; // CSS px: the hard visitor's window
export const PRIZE_BOX = { h: 150 }; // CSS px: the envelope or the keyring, in "your result"
function rayParts(win, el, boxH = PRIZE_BOX.h) {
  const k = ART_PX, cyBox = boxH / 2;
  const rays = el('span', 'bk-rays'), pic = el('span', 'bk-pic'), out = [rays, pic];
  if (win) {
    const b = BURST.big, o = place(OPEN, ZOOM.p, ZOOM.x, ZOOM.y), cx = (o.left + o.width / 2 - b.x) / k, cy = (WIN.title + o.top + o.height / 2 - b.y) / k;
    css(rays, { backgroundImage: svgUrl(burst('big')), width: b.w + 'px', height: b.h + 'px', left: `calc(50% - ${cx * k}px)`, top: cyBox - cy * k + 'px' });
    const stamp = el('span', 'bk-mark');
    css(stamp, { backgroundImage: svgUrl(mark('big')), width: MARK * 3 + 'px', height: MARK * 3 + 'px', left: 'calc(50% + 44px)', top: cyBox - 66 + 'px' });
    out.push(stamp);
  } else css(rays, { backgroundImage: svgUrl(glow('points')), width: VIEW.w * k + 'px', height: VIEW.h * k + 'px', left: `calc(50% - ${GLOW.cx * k}px)`, top: cyBox - GLOW.cy * k + 'px' });
  css(pic, { backgroundImage: svgUrl(opened(win ? 'big' : 'points')), width: OPEN.w * k + 'px', height: OPEN.h * k + 'px', left: `calc(50% - ${OPEN.w * k / 2}px)`, top: cyBox - OPEN.h * k / 2 + 'px' });
  return out;
}

function maker(doc) {
  // every sentence sits in a .t span: the page's filter cuts its glyphs to ink or paper (bigknock.css)
  return (tag, cls, text) => {
    const e = doc.createElement(tag);
    if (cls) e.className = cls;
    if (text) { const t = doc.createElement('span'); t.className = 't'; t.textContent = text; e.append(t); e.label = t; }
    return e;
  };
}
// A countdown's m:ss is in Jersey 10 (the art bible: numbers in the pixel font, words in the sentence font), the words around it are not. In a real
// document the number becomes a span of its own (.bk-num); textContent, what a reader sees, does not change. A test's small fake has no ownerDocument.
function numerals(t) {
  const d = t.ownerDocument, m = d && /\d+:\d\d/.exec(t.textContent);
  if (!m) return;
  const full = t.textContent, num = d.createElement('span');
  num.className = 'bk-num'; num.textContent = m[0];
  t.replaceChildren(d.createTextNode(full.slice(0, m.index)), num, d.createTextNode(full.slice(m.index + m[0].length)));
}
const setText = (e, text, clock = true) => { if (e.label.textContent === text) return; e.label.textContent = text; if (clock) numerals(e.label); };

// The hall banner: one strip under the top bar. `Big Knock #7` left, the live clock right, a See button. strip(): the same slot says the reveal is ready.
export function renderBanner(root, { doc, onSee }) {
  const el = maker(doc);
  const title = el('span', 'bk-title', C.banner.title(0)), clock = el('span', 'bk-clock', ' '), see = el('button', 'bk-see', C.banner.button);
  let onStrip = null;
  see.type = 'button'; see.onclick = () => (onStrip ? onStrip() : onSee());
  root.replaceChildren(title, clock, see);
  return { see,
    set(st, left) {
      if (onStrip) { onStrip = null; root.replaceChildren(title, clock, see); }
      setText(title, C.banner.title(st.n), false); setText(clock, clockLine(st, left)); setText(see, C.banner.button, false); root.hidden = false;
    },
    // "Big Knock #7 is revealed." and See the reveal: one line, in the banner's own slot, over a live door round
    strip(n, onTap) {
      onStrip = onTap; root.replaceChildren(title, see);
      setText(title, C.wait.ready(n), false); setText(see, C.wait.see, false); root.hidden = false;
    } };
}

// The door panel: the clock row, the crowd row (a different row), the rules, the truth line, Knock and Close.
export function renderPanel(root, { doc, onKnock, onClose }) {
  const el = maker(doc);
  const title = el('h2', 'bk-card-title'), plate = el('span', 'plate', C.banner.title(0)); title.replaceChildren(plate);
  const clock = el('p', 'bk-big', ' '), crowd = el('p', 'bk-crowd', ' '), line = el('p', 'bk-line', C.banner.line);
  const rules = el('ul', 'bk-rules');
  for (const r of C.rules) rules.append(el('li', '', r));
  const truth = el('p', 'bk-truth', C.truth), gate = el('p', 'bk-gate', C.gate + ' ' + C.draw(BIGKNOCK.minDoors)), note = el('p', 'bk-note', ' ');
  const knock = el('button', 'btn bk-go', C.join), close = el('button', 'btn', 'Close');
  knock.type = close.type = 'button';
  knock.onclick = () => onKnock(); close.onclick = () => onClose();
  const actions = el('div', 'bk-actions'); actions.append(knock, close);
  const card = el('div', 'bk-card'); card.append(title, clock, crowd, line, rules, gate, truth, note, actions);
  root.replaceChildren(card);
  return {
    knock, close, note,
    set(st, left) {
      setText(plate, C.banner.title(st.n), false); setText(clock, clockLine(st, left)); setText(crowd, C.crowd(st.crowd), false);
      const open = shownPhase(st, left) === 'open';
      knock.disabled = !(open && !st.joined);
      setText(knock, st.joined ? 'Already knocked' : open ? C.join : C.countdown.toOpen(left.toOpen));
    },
    say(text) { setText(note, text || ' ', false); },
  };
}

// The wait after your call, one card: the clock (doors still open, or the reveal's), how many have called and how many are at the door (rows of their
// own), your call is locked, the next Big Knock's window and why it is a window, and Play the door while you wait. It is only ever shown after the call,
// and nothing here is read by the event: the call is final and already stored.
export function renderWaiting(root, { doc, onPlay, now = () => Date.now(), tzAt = localTz, zoneAt = null }) {
  const el = maker(doc);
  const title = el('h2', 'bk-card-title'), plate = el('span', 'plate', C.banner.title(0)); title.replaceChildren(plate);
  const wait = el('p', 'bk-big', ' '), calls = el('p', 'bk-calls', ' '), crowd = el('p', 'bk-crowd', ' '), counts = el('p', 'bk-gate', ' ');
  const next = el('p', 'bk-next', ' '), rule = el('p', 'bk-rule', C.window.rule);
  const play = el('button', 'btn bk-go', C.wait.play); play.type = 'button'; play.onclick = () => onPlay();
  const actions = el('div', 'bk-actions'); actions.append(play);
  const card = el('div', 'bk-card'); card.append(title, wait, calls, crowd, counts, next, rule, actions);
  root.replaceChildren(card);
  return { play, set(st, left, called, nextWindow = null) {
    setText(plate, C.banner.title(st.n), false); setText(wait, waitClock(st, left)); setText(crowd, C.crowd(st.crowd), false);
    setText(calls, C.wait.callsIn(Math.min(st.calls ?? 0, st.crowd), st.crowd), false);
    setText(counts, called.counts ? C.wait.locked : called.timeout ? C.you.timeout : C.you.notCounted, false);
    setText(next, nextLine(nextWindow, now(), tzAt, zoneAt), false);
    rule.hidden = !nextWindow;
  } };
}

// The big reveal. m is the server's bk-reveal message. The hard visitor plays first in the art box, behind reeded glass (the door's own reveal for its kind:
// the alien stretching with red eyes, the neighbour walking off into the snow; client/bigknock-visitor.js, passed in as `visitor`), then "It was an alien.",
// then the crowd, then your result with the envelope or the keyring. set/clear: timers (injected for tests). Returns { show(step), steps, cancel }.
export function renderReveal(root, m, { doc, reduced = false, onShare, onClose, set = (fn, ms) => setTimeout(fn, ms), clear = id => clearTimeout(id), visitor = null, now = () => Date.now(), tzAt = localTz, zoneAt = null }) {
  const el = maker(doc);
  const you = youLines(m.you), win = m.you?.outcome === 'winner', outcome = m.you?.outcome;
  const title = el('h2', 'bk-card-title'); title.append(el('span', 'plate', C.banner.title(m.n)));
  const art = el('div', 'bk-art visitor ' + m.kind); art.dataset.kind = m.kind; art.setAttribute('aria-hidden', 'true');
  const stopVisitor = visitor ? visitor(art, { n: m.n, kind: m.kind, reduced }) : null;
  const kind = el('p', 'bk-step bk-kind', C.reveal.kind(m.kind));
  const crowd = el('p', 'bk-step bk-count', C.reveal.crowd(m.right, m.real));
  const drawn = m.winners > 0 ? el('p', 'bk-step bk-drawn', C.reveal.drawn(m.winners)) : null;
  const result = el('div', 'bk-step bk-you');
  let prize = null;
  if (win || outcome === 'right') { prize = el('div', 'bk-prize ' + (win ? 'win' : 'right')); prize.setAttribute('aria-hidden', 'true'); prize.append(...rayParts(win, el)); }
  else if (outcome === 'fooled') {
    prize = el('div', 'bk-prize fooled'); prize.setAttribute('aria-hidden', 'true');
    const ring = el('span', 'bk-pic keyring');
    const have = m.you.keyring?.have ?? 0; // the ring with its ten tags, at the stage's 2x (mail-art.js keyringRing)
    css(ring, { backgroundImage: svgUrl(keyringRing(have)) });
    ring.dataset.slots = String(ringSlots().length); ring.dataset.have = String(have);
    prize.append(ring);
  }
  // a keyring is only worth something if the player knows what it opens: the line is there with every fragment grant, signed in or not
  result.append(...(prize ? [prize] : []), el('p', 'bk-head', you.head), ...you.gain.map(g => el('p', 'bk-gain', g)), ...(outcome === 'fooled' ? [el('p', 'bk-gain bk-use', C.gain.keyringUse)] : []));
  const next = el('p', 'bk-next', nextLine(m.next, now(), tzAt, zoneAt)), rule = m.next ? el('p', 'bk-rule', C.window.rule) : null;
  const check = el('a', 'bk-verify', C.reveal.check); check.href = `/verify?bigknock=${m.n}`;
  const share = shareFor(m) ? el('button', 'btn bk-share', 'Share') : null;
  const back = el('button', 'btn', C.again);
  back.type = 'button'; back.onclick = () => onClose();
  if (share) { share.type = 'button'; share.onclick = () => onShare(shareFor(m)); }
  const actions = el('div', 'bk-actions'); actions.append(...(share ? [share] : []), back);
  // Share comes with the last step: nothing to post until the reveal has played out (sprint 31)
  const steps = { kind: [kind], crowd: [crowd, ...(drawn ? [drawn] : [])], you: [result, ...(share ? [share] : [])] };
  const all = [kind, crowd, ...(drawn ? [drawn] : []), result, ...(share ? [share] : [])];
  const card = el('div', 'bk-card bk-reveal' + (reduced ? ' still' : ''));
  card.append(title, art, kind, crowd, ...(drawn ? [drawn] : []), result, next, ...(rule ? [rule] : []), actions, check);
  root.replaceChildren(card);
  const timers = [];
  const show = what => { if (what === 'visitor') return; for (const e of what === 'all' ? all : steps[what]) e.hidden = false; };
  for (const e of all) e.hidden = !reduced;
  if (!reduced) for (const b of revealPlan({ reduced, kind: m.kind })) if (b.show !== 'visitor') timers.push(set(() => show(b.show), b.at));
  return { card, art, share, back, show, steps, cancel() { for (const t of timers) clear(t); if (typeof stopVisitor === 'function') stopVisitor(); } };
}
